import React, { useState } from 'react';
import type { PositionRule, AccountHolding, RuleExecution } from '../../types';
import LoadingSpinner from './LoadingSpinner';
import { toISTString } from '../../utils/timezone';

interface RulesManagementInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  exchange: string;
  companyName?: string;
  accounts: AccountHolding[];
  rules: PositionRule[];
  executions: RuleExecution[];
  onEditRule: (rule: PositionRule) => void;
  onDeleteRule: (ruleId: string) => Promise<void>;
  onToggleRule: (ruleId: string, isActive: boolean) => Promise<void>;
  onBulkToggle: (ruleIds: string[], isActive: boolean) => Promise<void>;
}

const RulesManagementInterface: React.FC<RulesManagementInterfaceProps> = ({
  isOpen,
  onClose,
  symbol,
  exchange,
  companyName,
  accounts,
  rules,
  executions,
  onEditRule,
  onDeleteRule,
  onToggleRule,
  onBulkToggle
}) => {
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [loading, setLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'rules' | 'history'>('rules');

  // Group rules by account
  const rulesByAccount = rules.reduce((acc, rule) => {
    const accountId = rule.rule_scope === 'specific_account' ? rule.target_account_id?.toString() || 'all' : 'all';
    if (!acc[accountId]) {
      acc[accountId] = [];
    }
    acc[accountId].push(rule);
    return acc;
  }, {} as Record<string, PositionRule[]>);

  const handleSelectRule = (ruleId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedRules(prev => [...prev, ruleId]);
    } else {
      setSelectedRules(prev => prev.filter(id => id !== ruleId));
    }
  };

  const handleSelectAll = (accountRules: PositionRule[], isSelected: boolean) => {
    const ruleIds = accountRules.map(rule => rule.id);
    if (isSelected) {
      setSelectedRules(prev => [...new Set([...prev, ...ruleIds])]);
    } else {
      setSelectedRules(prev => prev.filter(id => !ruleIds.includes(id)));
    }
  };

  const handleBulkAction = async (action: 'enable' | 'disable' | 'delete') => {
    if (selectedRules.length === 0) return;

    try {
      setLoading(`bulk-${action}`);
      
      if (action === 'delete') {
        await Promise.all(selectedRules.map(ruleId => onDeleteRule(ruleId)));
      } else {
        await onBulkToggle(selectedRules, action === 'enable');
      }
      
      setSelectedRules([]);
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
    } finally {
      setLoading(null);
    }
  };

  const handleToggleRule = async (ruleId: string, currentState: boolean) => {
    try {
      setLoading(ruleId);
      await onToggleRule(ruleId, !currentState);
    } catch (error) {
      console.error('Error toggling rule:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      setLoading(ruleId);
      await onDeleteRule(ruleId);
    } catch (error) {
      console.error('Error deleting rule:', error);
    } finally {
      setLoading(null);
    }
  };

  const getAccountName = (accountId: string) => {
    if (accountId === 'all') return 'All Holdings';
    const account = accounts.find(acc => acc.broker_account_id.toString() === accountId);
    return account ? `${account.broker_name} (${account.broker_type})` : 'Unknown Account';
  };

  const formatTrigger = (rule: PositionRule) => {
    const value = rule.trigger_value;
    const unit = rule.trigger_type === 'TRAILING_STOP_LOSS' ? '%' : '₹';
    return `${rule.trigger_type === 'TRAILING_STOP_LOSS' ? 'Trailing' : 'Fixed'} Stop Loss at ${value}${unit}`;
  };

  const formatAction = (rule: PositionRule) => {
    if (rule.action_type === 'ALERT') return 'Alert Only';
    
    let action = 'Sell ';
    if (rule.sell_quantity_type === 'ALL_REMAINING') {
      action += 'All';
    } else if (rule.sell_quantity_type === 'PERCENTAGE') {
      action += `${rule.sell_quantity_value}%`;
    } else {
      action += `${rule.sell_quantity_value} shares`;
    }
    action += ` (${rule.order_type})`;
    return action;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Manage Position Rules
                </h3>
                <p className="text-sm text-gray-500">
                  {symbol} ({exchange}) {companyName && `- ${companyName}`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('rules')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'rules'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Rules ({rules.length})
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'history'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Execution History ({executions.length})
                </button>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 max-h-96 overflow-y-auto">
            {activeTab === 'rules' && (
              <div className="space-y-6">
                {/* Bulk Actions */}
                {selectedRules.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-800">
                        {selectedRules.length} rule(s) selected
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleBulkAction('enable')}
                          disabled={loading === 'bulk-enable'}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {loading === 'bulk-enable' ? <LoadingSpinner /> : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleBulkAction('disable')}
                          disabled={loading === 'bulk-disable'}
                          className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 disabled:opacity-50"
                        >
                          {loading === 'bulk-disable' ? <LoadingSpinner /> : 'Disable'}
                        </button>
                        <button
                          onClick={() => handleBulkAction('delete')}
                          disabled={loading === 'bulk-delete'}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {loading === 'bulk-delete' ? <LoadingSpinner /> : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rules by Account */}
                {Object.entries(rulesByAccount).map(([accountId, accountRules]) => (
                  <div key={accountId} className="border border-gray-200 rounded-lg">
                    {/* Account Header */}
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">
                          {getAccountName(accountId)} ({accountRules.length} rules)
                        </h4>
                        <label className="flex items-center text-sm text-gray-600">
                          <input
                            type="checkbox"
                            checked={accountRules.every(rule => selectedRules.includes(rule.id))}
                            onChange={(e) => handleSelectAll(accountRules, e.target.checked)}
                            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          Select All
                        </label>
                      </div>
                    </div>

                    {/* Rules List */}
                    <div className="divide-y divide-gray-200">
                      {accountRules.map((rule) => (
                        <div key={rule.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedRules.includes(rule.id)}
                                onChange={(e) => handleSelectRule(rule.id, e.target.checked)}
                                className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className="font-medium text-gray-900">{rule.rule_name}</h5>
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    rule.is_active 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {rule.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  {rule.execution_count > 0 && (
                                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                      {rule.execution_count} executions
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {formatTrigger(rule)} → {formatAction(rule)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Created: {toISTString(rule.created_at)} |  
                                  Priority: {rule.priority}
                                  {rule.last_triggered_at && (
                                    <> | Last triggered: {toISTString(rule.last_triggered_at)}</>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Rule Actions */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleToggleRule(rule.id, rule.is_active)}
                                disabled={loading === rule.id}
                                className={`px-3 py-1 text-sm rounded transition-colors ${
                                  rule.is_active
                                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                {loading === rule.id ? (
                                  <LoadingSpinner />
                                ) : rule.is_active ? (
                                  'Disable'
                                ) : (
                                  'Enable'
                                )}
                              </button>
                              <button
                                onClick={() => onEditRule(rule)}
                                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded hover:bg-blue-200 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteRule(rule.id)}
                                disabled={loading === rule.id}
                                className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded hover:bg-red-200 transition-colors"
                              >
                                {loading === rule.id ? <LoadingSpinner /> : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {rules.length === 0 && (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Rules Found</h3>
                    <p className="text-gray-600">Create your first position rule to get started.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-4">
                {executions.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Execution History</h3>
                    <p className="text-gray-600">Rule executions will appear here once triggered.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {executions.map((execution) => (
                      <div key={execution.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                execution.status === 'SUCCESS' 
                                  ? 'bg-green-100 text-green-800'
                                  : execution.status === 'FAILED'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {execution.status}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {execution.execution_type === 'SELL_ORDER' ? 'Sell Order' : 'Alert Sent'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              Triggered at ₹{execution.trigger_price} | 
                              Account: {execution.account_id ? getAccountName(execution.account_id.toString()) : 'Unknown'}
                            </div>
                            {execution.quantity_executed && (
                              <div className="text-sm text-gray-600">
                                Executed: {execution.quantity_executed} shares at ₹{execution.execution_price}
                              </div>
                            )}
                            {execution.broker_order_id && (
                              <div className="text-xs text-gray-500">
                                Broker Order ID: {execution.broker_order_id}
                              </div>
                            )}
                            {execution.error_message && (
                              <div className="text-sm text-red-600 mt-1">
                                Error: {execution.error_message}
                              </div>
                            )}
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            {execution.executed_at}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RulesManagementInterface; 